import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import PricingToggle from './components/PricingToggle';
import PricingCard from './components/PricingCard';
import Skeleton from '../../components/ui/Skeleton';
import ComparisonTable from './components/ComparisonTable';
import EnterpriseSection from './components/EnterpriseSection';
import FAQSection from './components/FAQSection';

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const toggleBillingCycle = () => {
    setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly');
  };

  const [pricingPlans, setPricingPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setPlansLoading(true);
    fetch('https://api.cultaz.com/API/plans.php')
      .then(r => r.json())
          .then(data => {
        if (!mounted) return;
        if (data?.status && Array.isArray(data.plans)) {
          // Map remote plan shape to local PricingCard shape
          const mapped = data.plans.map(p => ({
            name: p.name,
            description: p.description,
            type: 'cloud',
            monthlyPrice: Number(p.price) || 0,
            yearlyPrice: (Number(p.price) || 0) * 12,
                ctaText: 'Choose',
                ctaLink: 'https://app.cultaz.com',
            featuresTitle: 'Includes',
            features: [
              `POS: ${p.features?.pos?.active ? 'Yes' : 'No'}`,
              `Products: ${p.features?.products?.active ? p.features?.products?.value : '-'}`,
              `Support: ${p.features?.support?.active ? 'Yes' : 'No'}`,
              `Installable PWA - Yes`
            ]
          }));
          setPricingPlans(mapped);
        } else {
          setPlansError('Invalid plans response');
        }
      })
      .catch(() => setPlansError('Unable to load plans'))
      .finally(() => mounted && setPlansLoading(false));
    return () => { mounted = false; };
  }, []);

  return (
    <>
      <Helmet>
        <title>Pricing - Cultaz | Transparent Retail Management Pricing Plans in NGN</title>
        <meta
          name="description"
          content="Compare Cultaz pricing plans. Choose between cloud subscriptions for multi-store management or monthly desktop licensing for offline reliability. Transparent pricing in Nigerian Naira (NGN). Free trial available."
        />
        <meta
          name="keywords"
          content="Cultaz pricing, retail software pricing, POS system cost, inventory management pricing, Nigeria retail software price, cloud POS pricing, desktop POS license, Nigerian Naira pricing"
        />
        <meta name="author" content="Cultaz" />
        <meta property="og:title" content="Pricing - Cultaz | Flexible Retail Management Solutions" />
        <meta property="og:description" content="Compare Cultaz pricing plans - cloud subscriptions or monthly desktop licensing. Transparent pricing in NGN for Nigerian retailers. Free trial available." />
        <meta property="og:image" content="https://cultaz.com/banners/1.png" />
        <meta property="og:url" content="https://cultaz.com/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cultaz" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pricing - Cultaz | Retail Management Pricing Plans" />
        <meta name="twitter:description" content="Compare Cultaz pricing plans - cloud subscriptions or monthly desktop licensing. Transparent pricing in NGN." />
        <meta name="twitter:image" content="https://cultaz.com/banners/1.png" />
        <link rel="canonical" href="https://cultaz.com/pricing" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main>
          {/* Hero Section */}
          <section className="py-12 md:py-16 lg:py-24 px-4 md:px-6 lg:px-8">
            <div className="container mx-auto">
              <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 md:mb-6">
                  Simple, Transparent Pricing
                </h1>
                <p className="font-body text-base md:text-lg lg:text-xl text-muted-foreground mb-8 md:mb-12">
                  Choose the perfect plan for your retail business. Whether you need cloud flexibility or offline reliability, we've got you covered.
                </p>
                <PricingToggle billingCycle={billingCycle} onToggle={toggleBillingCycle} />
              </div>

              {/* Pricing Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-16 md:mb-24">
                {plansLoading ? (
                  <Skeleton count={3} />
                ) : plansError ? (
                  <div className="col-span-1 md:col-span-2 lg:col-span-3 p-6 rounded-md border bg-card text-destructive">{plansError}</div>
                ) : (
                  pricingPlans?.map((plan, index) => (
                    <PricingCard
                      key={index}
                      plan={plan}
                      billingCycle={billingCycle}
                      isPopular={index === 1}
                    />
                  ))
                )}

                {/* Desktop license calculator removed per request */}
              </div>
            </div>
          </section>

          {/* Comparison Table Section */}
          <section className="py-12 md:py-16 lg:py-24 px-4 md:px-6 lg:px-8 bg-muted/30">
            <div className="container mx-auto">
              <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                  Compare All Features
                </h2>
                <p className="font-body text-base md:text-lg text-muted-foreground">
                  See exactly what's included in each solution to make the best choice for your business needs.
                </p>
              </div>
              <ComparisonTable />
            </div>
          </section>

          {/* Enterprise Section */}
          <section className="py-12 md:py-16 lg:py-24 px-4 md:px-6 lg:px-8">
            <div className="container mx-auto">
              <EnterpriseSection />
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-12 md:py-16 lg:py-24 px-4 md:px-6 lg:px-8 bg-muted/30">
            <div className="container mx-auto">
              <FAQSection />
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-12 md:py-16 lg:py-24 px-4 md:px-6 lg:px-8">
            <div className="container mx-auto">
              <div className="rounded-2xl bg-gradient-to-br from-primary to-secondary p-8 md:p-12 lg:p-16 text-center">
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-4">
                  Ready to Transform Your Retail Business?
                </h2>
                <p className="font-body text-base md:text-lg text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
                  Join thousands of Nigerian retailers who trust Cultaz for their business management needs. Start your free trial today or purchase a lifetime desktop license.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a
                    href="https://app.cultaz.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                  >
                    <button className="px-8 py-4 bg-card text-foreground rounded-lg font-body text-base font-medium transition-smooth hover:scale-105 hover:shadow-purple-lg focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3">
                      Start Free Trial
                    </button>
                  </a>
                  <Link to="/support" className="inline-block">
                    <button className="px-8 py-4 bg-transparent border-2 border-primary-foreground text-primary-foreground rounded-lg font-body text-base font-medium transition-smooth hover:bg-primary-foreground hover:text-primary focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3">
                      Contact Sales
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Pricing;